/**
 * Turns the Sumator lines the bridge reports into lines the widget can show.
 *
 * During the transition the Delphi application is still the system of record.
 * There, the form that performs an operation appends its own line to the
 * Contracts form's Sumator memo (`Ucontracte.pas` `memSumator`) - a new
 * contract, a redemption, an extension or a goods sale. A bridged operation has
 * no form in this app, so nothing appended anything and the widget never opened
 * after a legacy operation.
 *
 * The backend derives the lines (`domain/legacyBridge/sumatorLines.ts`); this
 * module owns the two rules that live on the client: what order they are
 * appended in, and remembering which events have already been appended so a
 * re-poll cannot add the same line twice.
 *
 * Kept pure so both rules are unit-testable without a clock or a rendered
 * component, the same way `bridgeActivity.ts` is.
 *
 * Reference: documentation/implementation_plan_legacy_delphi_bridge_api.md (section 7.7)
 */

import { parseTimestamp } from './bridgeActivity';

/** The Delphi label a bridged line carries, as the backend classifies it. */
export type BridgeSumatorKind = 'newContract' | 'payment' | 'extension' | 'goodsSale';

export interface BridgeSumatorLine {
    eventId: string;
    operation: string;
    kind: BridgeSumatorKind;
    /** Positive is money the client hands over, negative is money the shop pays out. */
    amount: number;
    contractNr: number | null;
    reference: string | null;
    contractNumber: string | null;
    cashPointId: number;
    occurredAt: string;
    receivedAt: string;
}

export interface BridgeSumatorResponse {
    enabled: boolean;
    serverTime: string;
    lines: BridgeSumatorLine[];
}

/** One translation key per kind, so no key is assembled from a variable. */
export const BRIDGE_SUMATOR_LABEL_KEY: Record<BridgeSumatorKind, string> = {
    newContract: 'sumator.bridge.newContract',
    payment: 'sumator.bridge.payment',
    extension: 'sumator.bridge.extension',
    goodsSale: 'sumator.bridge.goodsSale',
};

/**
 * How many consumed event ids are remembered.
 *
 * Only has to outlast the server's re-read window, which is a minute of events
 * - a few at the busiest. This is two orders of magnitude above that, and
 * bounding it at all is what stops a till left open all day from growing the
 * list without limit.
 */
export const MAX_CONSUMED_EVENT_IDS = 500;

/**
 * The lines to append, oldest first, with duplicates inside the batch dropped.
 *
 * Sorted here rather than trusted from the response for the same reason
 * `newestCall` re-derives the newest call: the server's ordering is not
 * something the widget should be wrong about if it ever changes. A row whose
 * `receivedAt` cannot be read is dropped - it cannot be placed in the sequence,
 * and guessing a position could show a redemption before the contract it pays.
 */
export const orderBridgeSumatorLines = (lines: BridgeSumatorLine[]): BridgeSumatorLine[] => {
    const seen = new Set<string>();
    const dated: { line: BridgeSumatorLine; receivedAtMs: number }[] = [];

    for (const line of lines) {
        if (!line?.eventId || seen.has(line.eventId)) {
            continue;
        }
        const receivedAtMs = parseTimestamp(line.receivedAt);
        if (receivedAtMs === null) {
            continue;
        }
        seen.add(line.eventId);
        dated.push({ line, receivedAtMs });
    }

    dated.sort((left, right) => left.receivedAtMs - right.receivedAtMs);

    return dated.map(entry => entry.line);
};

/**
 * The line's description: the Delphi label, then what it is about.
 *
 * Delphi writes `Achitare : 29.07.2026-88213 : 1234`; the widget renders the
 * amount in its own column, so only the first two parts belong here. A line
 * whose reference could not be resolved keeps the bare label rather than
 * showing a dangling separator.
 */
export const formatBridgeSumatorDescription = (label: string, reference: string | null): string =>
    reference ? `${label} ${reference}` : label;

/**
 * The consumed-id list after appending `added`, capped at
 * {@link MAX_CONSUMED_EVENT_IDS} by dropping the oldest.
 */
export const rememberConsumedEventIds = (
    consumed: readonly string[],
    added: readonly string[],
): string[] => {
    const next = [...consumed, ...added];
    return next.length <= MAX_CONSUMED_EVENT_IDS
        ? next
        : next.slice(next.length - MAX_CONSUMED_EVENT_IDS);
};
